import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Badge } from "@foundation/components/ui/badge";
import {
  ArrowRight,
  ArrowLeft,
  FileEdit,
  FileSearch,
  Palette,
  Sparkles,
  Shield,
  CheckCircle,
  Zap,
  Target,
  TrendingUp,
  Clock,
  Wand2,
  FileType,
  Paintbrush,
  Lock,
} from "lucide-react";
import ScopingAILayout from "../components/RevisrLayout";
import { useSubscriptionUsage } from "../hooks/useSubscriptionUsage";

const AuditWorkflowSelection: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const { canCreatePolicy, policyUsage } = useSubscriptionUsage();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const workflowOptions = [
    {
      id: "create_policy",
      title: "Power Up Your Policy",
      subtitle: "AI-Powered Formatting",
      description: "Upload your policy document, pick a template, and let AI format it into a professional document",
      icon: FileEdit,
      secondaryIcon: Sparkles,
      color: "from-emerald-400 via-green-500 to-teal-600",
      glowColor: "shadow-green-500/25",
      features: [
        { icon: Clock, text: "Quick formatting" },
        { icon: Wand2, text: "Template selection" },
        { icon: Target, text: "Professional output" },
        { icon: Zap, text: "AI-powered" }
      ],
      comingSoon: false,
      disabled: !canCreatePolicy,
      limitReached: !canCreatePolicy,
      limitInfo: policyUsage ? { current: policyUsage.current, limit: policyUsage.limit } : null,
      onClick: () => {
        if (!canCreatePolicy) return;
        // Clear any previous create_policy workflow data
        sessionStorage.removeItem('create_policy_policy_review_id');
        sessionStorage.removeItem('create_policy_generated_content');
        sessionStorage.removeItem('create_policy_template_id');
        sessionStorage.removeItem('create_policy_template_name');
        sessionStorage.removeItem('create_policy_policy_name');

        // Set workflow type
        sessionStorage.setItem('workflow_type', 'create_policy');
        navigate("/revisr/create-policy");
      }
    },
    {
      id: "review_policy",
      title: "Review Existing Policy",
      subtitle: "Compare & Analyze",
      description: "Compare your policy against reference documents and identify differences",
      icon: FileSearch,
      secondaryIcon: Shield,
      color: "from-blue-400 via-indigo-500 to-purple-600",
      glowColor: "shadow-blue-500/25",
      features: [
        { icon: CheckCircle, text: "AI comparison" },
        { icon: TrendingUp, text: "Change detection" },
        { icon: Shield, text: "Compliance check" },
        { icon: Target, text: "Detailed review" }
      ],
      comingSoon: false,
      disabled: false,
      onClick: () => {
        // Clear any previous workflow data
        sessionStorage.removeItem('auditComparisonData');
        sessionStorage.removeItem('selectedIndustry');
        sessionStorage.removeItem('selectedTemplate');
        sessionStorage.removeItem('selectedReference');
        sessionStorage.removeItem('detectedChanges');
        sessionStorage.removeItem('reviewedChanges');
        
        // Set workflow type
        sessionStorage.setItem('workflow_type', 'review_policy');
        navigate("/revisr/new-audit");
      }
    },
    {
      id: "brand_policy",
      title: "Brand Your Policy",
      subtitle: "AI-Powered Branding",
      description: "Paste your policy content, pick a template, and let AI restructure & brand it with your organisation's identity",
      icon: Palette,
      secondaryIcon: Paintbrush,
      color: "from-purple-400 via-violet-500 to-indigo-600",
      glowColor: "shadow-purple-500/25",
      features: [
        { icon: FileType, text: "Template mapping" },
        { icon: Paintbrush, text: "Brand application" },
        { icon: Sparkles, text: "AI restructuring" },
        { icon: Target, text: "Professional output" }
      ],
      comingSoon: false,
      disabled: false,
      onClick: () => {
        sessionStorage.removeItem('revisr_brand_policy_data');
        sessionStorage.removeItem('revisr_brand_policy_result');

        sessionStorage.setItem('workflow_type', 'brand_policy');
        navigate("/revisr/brand-policy");
      }
    }
  ];

  return (
    <ScopingAILayout>
      <div className="min-h-screen bg-[#FCFBF4] relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#33b864]/20 to-[#2a9d54]/20 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-[#33b864]/15 to-[#FCFBF4]/30 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>

        {/* Header Section */}
        <motion.div
          className="relative z-10 bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-lg"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <motion.div
              className="flex items-center justify-between"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/revisr/dashboard')}
                  className="gap-2 hover:bg-white/50 transition-all duration-300 group mb-4"
                >
                  <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform duration-300" />
                  Back to Dashboard
                </Button>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Choose Your Workflow</h1>
                <p className="text-gray-600 text-sm md:text-base">Select how you want to work with your policy document</p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Options Section */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {workflowOptions.map((option, index) => {
              const IconComponent = option.icon;
              const SecondaryIcon = option.secondaryIcon;

              return (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 * index }}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                  onHoverStart={() => setHoveredCard(option.id)}
                  onHoverEnd={() => setHoveredCard(null)}
                >
                  <Card
                    className={`relative overflow-hidden transition-all duration-500 group cursor-pointer h-full
                      ${option.disabled
                        ? 'opacity-60 cursor-not-allowed'
                        : `hover:shadow-2xl hover:${option.glowColor} border-0 bg-white/90 backdrop-blur-sm`
                      }`}
                    onClick={option.disabled ? undefined : option.onClick}
                  >
                    {/* Gradient Border Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                         style={{ padding: '2px' }}>
                      <div className="h-full w-full bg-white rounded-lg"></div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 p-8">
                      {/* Limit Reached Banner */}
                      {(option as any).limitReached && (
                        <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-4">
                          <Lock className="h-4 w-4 text-orange-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-orange-700">Policy limit reached</p>
                            {(option as any).limitInfo?.limit !== undefined && (
                              <p className="text-xs text-orange-600">
                                {(option as any).limitInfo.current} / {(option as any).limitInfo.limit} used — upgrade to create more
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Header with Icons */}
                      <div className="mb-6">
                        <div className="flex items-center gap-4 mb-4">
                          <motion.div
                            className={`relative w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${
                              option.id === 'create_policy'
                                ? (option as any).limitReached ? 'bg-gray-400' : 'bg-[#33b864]'
                                : option.id === 'brand_policy'
                                ? 'bg-purple-500'
                                : 'bg-blue-500'
                            }`}
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                          >
                            <IconComponent className="h-8 w-8 text-white" />
                            <motion.div
                              className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.3 + index * 0.1 }}
                            >
                              <SecondaryIcon className={`h-3 w-3 ${
                                option.id === 'create_policy'
                                  ? (option as any).limitReached ? 'text-gray-400' : 'text-[#33b864]'
                                  : option.id === 'brand_policy'
                                  ? 'text-purple-600'
                                  : 'text-blue-600'
                              }`} />
                            </motion.div>
                          </motion.div>
                        </div>

                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-1">
                            {option.title}
                          </h3>
                          <p className="text-sm font-medium text-gray-500 mb-3">
                            {option.subtitle}
                          </p>
                          <p className="text-gray-600 leading-relaxed">
                            {option.description}
                          </p>
                        </div>
                      </div>

                      {/* Features List */}
                      <div className="space-y-3 mb-6">
                        {option.features.map((feature, featureIndex) => {
                          const FeatureIcon = feature.icon;
                          return (
                            <motion.div
                              key={featureIndex}
                              className="flex items-center gap-3"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.6 + featureIndex * 0.1 }}
                            >
                              <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                                option.id === 'create_policy'
                                  ? 'bg-[#33b864]'
                                  : option.id === 'brand_policy'
                                  ? 'bg-purple-500'
                                  : 'bg-blue-500'
                              }`}>
                                <FeatureIcon className="h-3 w-3 text-white" />
                              </div>
                              <span className="text-sm text-gray-700 font-medium">{feature.text}</span>
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* Action Button */}
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Button
                          className={`w-full h-12 text-white font-semibold rounded-xl transition-all duration-300 ${
                            option.disabled
                              ? 'bg-gray-400 cursor-not-allowed'
                              : option.id === 'create_policy'
                                ? 'bg-[#33b864] hover:bg-[#2a9d54] hover:shadow-lg'
                                : option.id === 'brand_policy'
                                ? 'bg-purple-500 hover:bg-purple-600 hover:shadow-lg'
                                : 'bg-blue-500 hover:bg-blue-600 hover:shadow-lg'
                          }`}
                          disabled={option.disabled}
                          onClick={option.disabled ? undefined : option.onClick}
                        >
                          {(option as any).limitReached ? (
                            <>
                              <Lock className="h-4 w-4 mr-2 text-white" />
                              Limit Reached
                            </>
                          ) : (
                            <>
                              Get Started
                              <ArrowRight className="h-4 w-4 ml-2 text-white" />
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </div>

                    {/* Hover Effect Overlay */}
                    <motion.div
                      className={`absolute inset-0 bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                      initial={false}
                    />
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </ScopingAILayout>
  );
};

export default AuditWorkflowSelection;

